import type { IEditorVariable } from "@bbj-types/utils";
import type { Editor } from "tinymce";
import { createListVariableSpan, createSimpleVariableSpan } from "./variableTransformation";

/**
 * Creates menu items for simple variables
 */
const createSimpleMenuItems = (variableList: IEditorVariable[], insertFn: (content: string) => void) => {
  return variableList.map((v) => ({
    type: "menuitem" as const,
    text: v.label,
    onAction: () => insertFn(createSimpleVariableSpan(v.label, v.value)),
  }));
};

/**
 * Creates menu items for list variables
 */
const createListMenuItems = (listVariables: IEditorVariable[], insertFn: (content: string) => void) => {
  return listVariables
    .filter((lv) => lv.variables) // Only include variables that have the variables field
    .map((lv) => {
      const fieldsDescription = lv.variables?.map((v) => `[${v.label}]`).join(", ") || "";
      return {
        type: "menuitem" as const,
        text: `${lv.label}\n   ${fieldsDescription}`,
        onAction: () => insertFn(createListVariableSpan(lv)),
      };
    });
};

/**
 * Creates the complete menu structure for variables
 */
export const createVariableMenuItems = (variableList?: IEditorVariable[], insertFn?: (content: string) => void) => {
  if (!insertFn) {
    return [];
  }

  // Partition variables into simple and list based on presence of variables field
  const simpleVariables = variableList?.filter((v) => !v.variables) || [];
  const listVariables = variableList?.filter((v) => v.variables) || [];

  const simpleItems = createSimpleMenuItems(simpleVariables, insertFn);
  const listItems = createListMenuItems(listVariables, insertFn);

  const items = [
    ...(simpleItems.length
      ? [
          {
            type: "separator" as const,
            text: "Ajouter une variable",
          },
          ...simpleItems,
        ]
      : []),
    ...(listItems.length
      ? [
          ...(simpleItems.length ? [{ type: "separator" as const }] : []),
          {
            type: "separator" as const,
            text: "Ajouter une liste d'éléments",
          },
          ...listItems,
        ]
      : []),
  ];

  return items;
};

/**
 * Registers the variable menu button with TinyMCE editor
 */
export const registerVariableMenuButton = (editor: Editor, variableList?: IEditorVariable[]): void => {
  if (variableList && variableList.length > 0) {
    editor.ui.registry.addMenuButton("variable", {
      icon: "add-variable",
      fetch: (callback) => {
        const insertFn = (content: string) => editor.insertContent(content);
        const items = createVariableMenuItems(variableList, insertFn);
        callback(items);
      },
    });
  }
};
